/**
 * Leaving as JSX because there's a bunch of type errors within this file.
 */
import { createContext, useContext, useMemo } from 'react'
import rehypeMathjax from 'rehype-mathjax'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'

// project import
import { CodeBlock, CollapsibleContent, MemoizedReactMarkdown } from 'torqcloud-ui'

import { markdownUrlTransform } from '@/utils/markdownUrl'
import { normalizeMathDelimiters } from '@/utils/normalizeMathDelimiters'
import { handleSanitizedCopy } from '@/utils/sanitizedCopy'

import { MarkdownImage, MarkdownLink } from './Chat.MarkdownElements'

export type MarkdownAnswerProps = {
  children: string | Record<string, unknown> | number | boolean | any[] | undefined
  downloadName: string
  isDialog?: boolean
}

export const MarkdownCollapseContext = createContext(true)

const MarkdownAnswer = ({ children, downloadName, isDialog }: MarkdownAnswerProps) => {
  const collapseMarkdown = useContext(MarkdownCollapseContext)
  const formattedChildren: string = useMemo(() => {
    let result: string

    if (Array.isArray(children) && children.every(m => m instanceof Object && 'type' in m)) {
      // This message appears to be a multi-part content message
      const parts: string[] = []
      for (const child of children) {
        switch (child.type) {
          case 'text':
            if (typeof child.text === 'string') {
              parts.push(child.text)
              continue
            }
            break
          case 'image_url':
            if (typeof child.image_url?.url === 'string') {
              parts.push(`![Image](${child.image_url?.url})`)
              continue
            }
            break
        }

        parts.push(wrapInCodeBlock(child))
      }

      result = parts.join('\n\n')
    } else {
      result = typeof children === 'string' ? children : wrapInCodeBlock(children)
    }

    return normalizeMathDelimiters(result)
  }, [children])

  const remarkMathOptions = {
    singleDollarTextMath: false,
  }

  const codeBlock = {
    nextId: 1,
  }

  const markdown = (
    // Copies made from rendered answers keep their structure but drop the
    // surrounding styling, so they paste cleanly into Word (WF-2609). Chat
    // handles this at the message-list level; this covers the answers rendered
    // outside a Chat, such as Team run outputs.
    <div data-component="markdown__container" onCopy={handleSanitizedCopy}>
      <MemoizedReactMarkdown
        remarkPlugins={[remarkGfm, [remarkMath, remarkMathOptions]]}
        rehypePlugins={[rehypeMathjax]}
        urlTransform={markdownUrlTransform}
        components={{
          code({ node, children, ...props }) {
            let className = ''
            if (Array.isArray(node?.properties.className)) {
              className = node.properties.className.join(' ')
            } else if (typeof node?.properties.className === 'string') {
              className = node.properties.className
            }
            const match = /language-(\w+)/.exec(className)
            return className ? (
              <CodeBlock
                key={Math.random()}
                downloadName={`${downloadName}-${codeBlock.nextId++}`}
                isDialog={!!isDialog}
                language={(match && match[1]) || ''}
                value={String(children).replace(/\n$/, '')}
                {...props}
              />
            ) : (
              <code {...props}>{children}</code>
            )
          },
          a: MarkdownLink,
          img: MarkdownImage,
        }}
      >
        {formattedChildren}
      </MemoizedReactMarkdown>
    </div>
  )

  if (!collapseMarkdown) {
    return <div className="markdownanswer">{markdown}</div>
  }

  return (
    <CollapsibleContent className="markdownanswer" maxHeight={300}>
      {markdown}
    </CollapsibleContent>
  )
}

const wrapInCodeBlock = (value: Record<string, unknown> | number | boolean | any[] | undefined) => {
  if (value === undefined) {
    return ''
  }
  try {
    const prettyJson = JSON.stringify(value, null, 2)
    return `\`\`\`json\n${prettyJson}\n\`\`\`` // Triple backticks for Markdown code block
  } catch (err) {
    return `\`\`\`\n${value}\n\`\`\``
  }
}

export default MarkdownAnswer
